import React, { useEffect, useRef, useCallback } from "react";
import { DateTimeUtil, getNotificationStatusLabel } from "../../utils";
import { Loader2, User } from "lucide-react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { decrementUnreadNotifCount } from "../../redux/features/common/slice";
import { setSelectedCategoryForDetails } from "../../redux/features/notifications/slice";
import { useAppDispatch, RootState } from "../../redux/store";
import { MobileNotification } from "../../types";
import { getNotifications } from "../../redux/features/mobileNotifications";
import { getNotificationDetail } from "../../redux/features/notifications";

const NotificationsWeb: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { isNotificationsLoading, notifications, notificationsMeta } = useSelector(
        (state: RootState) => state.mobileNotifications
    );
    const user = useSelector((state: RootState) => state.user.user);

    const scrollContainerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        // Initial page fetch
        dispatch(getNotifications({ page: 1 }));
    }, [dispatch]);

    const loadMore = useCallback(() => {
        const loadedCount = notifications.length;
        const totalCount = notificationsMeta?.count ?? 0;
    
        // If we've loaded all notifications, do nothing
        if (loadedCount >= totalCount) return;
    
        const nextPage = (notificationsMeta?.page ?? 1) + 1;
        dispatch(getNotifications({ page: nextPage }));
    }, [dispatch, notificationsMeta?.page, notificationsMeta?.count, notifications.length]);    

    const handleScroll = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container || isNotificationsLoading) return;

        const { scrollTop, scrollHeight, clientHeight } = container;

        // If near the bottom, trigger loading
        if (scrollTop + clientHeight >= scrollHeight - 100) {
            loadMore();
        }
    }, [isNotificationsLoading, loadMore]);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) return;

        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
    }, [handleScroll]);

    const handleToDetails = (notification: MobileNotification) => {
        dispatch(setSelectedCategoryForDetails(notification.notification_category));
        dispatch(decrementUnreadNotifCount());
        dispatch(getNotificationDetail({
            notificationId: notification.notification_id!,
            employeeId: Number(user.os_emp_account_id!),
        }));
        navigate("/notificationDetail");
    };

    return (
        <div className="mx-auto border-b bg-white h-full flex flex-col">
            <h2 className="text-2xl font-semibold text-gray-900 p-4">Notifications</h2>
            {isNotificationsLoading && notifications.length === 0 ? (
                <div className="flex justify-center items-center flex-1">
                    <Loader2 className="w-8 h-8 text-gray-600 animate-spin" />
                </div>
            ) : notifications.length === 0 ? (
                <p className="text-gray-500">No notifications found.</p>
            ) : (
                <div
                    ref={scrollContainerRef}
                    className="overflow-y-auto flex-1 flex flex-col"
                >
                    {notifications.map((notification) => {
                        const title = notification.message_info?.title || "Notification";
                        const body = notification.message_info?.body || "You have a new notification.";
                        const bgColor = notification.notification_read_status === "0" ? "bg-gray-100" : "bg-white";
                        const currentStatus = notification.workflow_current_step_details?.current_status;
                        const statusInfo = getNotificationStatusLabel(currentStatus);

                        return (
                            <div
                                key={notification.notification_id}
                                className={`p-4 border-b flex items-start space-x-4 hover:bg-gray-100 transition cursor-pointer ${bgColor}`}
                                onClick={() => handleToDetails(notification)}
                            >
                                {notification.requestor?.image_url ? (
                                    <img
                                        src={notification.requestor.image_url}
                                        alt={notification.requestor.emp_name}
                                        className="w-12 h-12 rounded-full object-cover"
                                    />
                                ) : (
                                    <User className="w-12 h-12 text-gray-500" />
                                )}
                                <div className="flex-1">
                                    <h3 className="font-medium text-gray-900">{title}</h3>
                                    <p className="text-sm text-gray-700">{body}</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        {DateTimeUtil.FormatWithDayjsUtc(
                                            notification.notification_date_time,
                                            DateTimeUtil.DATETIME_FANCY_2
                                        ) || "N/A"}{" "}
                                        | <span className={`${statusInfo.color}`}>{notification?.workflow_current_step_details?.status_text}</span>
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                    {isNotificationsLoading && (
                        <div className="flex justify-center items-center py-4">
                            <Loader2 className="w-6 h-6 animate-spin text-gray-600" />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationsWeb;
